import { useState, useEffect, useMemo } from 'react';
import Lightbox from './Lightbox';

// Static fallback data in case CMS is initialising or offline
const FALLBACK_GALLERY = [
  {
    title: 'Altan med glas',
    gallery_slug: 'altan',
    category: 'Altan',
    images: [
      'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/altan-med-glas-11024x768-911f0a0581-58-1000x750.jpg',
      'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/altan-4-310.jpg'
    ]
  },
  {
    title: 'Stålkonstruktioner',
    gallery_slug: 'staalkonstruktioner',
    category: 'Konstruktioner',
    images: [
      'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/staalspaer-lejligheder-11024x576-2d10694eef-106-1000x563.jpg',
      'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/staalkonstruktion-israels-plads-342.jpeg'
    ]
  },
  {
    title: 'Rækværk',
    gallery_slug: 'raekvaerk',
    category: 'Rækværk',
    images: [
      'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/raekvaerk-valby-station1024x768-0ae2c60169-150-1000x750.jpg',
      'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/raekvaerk-israels-plads1024x683-f5738afa2a-146.jpg'
    ]
  },
  {
    title: 'Trapper',
    gallery_slug: 'trapper',
    category: 'Trapper',
    images: [
      'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/haveje-trappe-og-vaern-7-330-1000x750.jpg',
      'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/trappe-vedbaek-stran-21024x768-911f0a0581-162.jpg'
    ]
  },
  {
    title: 'Porte og skydeporte',
    gallery_slug: 'porte',
    category: 'Skydeport',
    images: [
      'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/skydeport1024x768-eabe017ad6-154-1000x750.jpg',
      'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/port-1-282.jpg'
    ]
  },
  {
    title: 'Hegn',
    gallery_slug: 'hegn',
    category: 'Hegn',
    images: [
      'https://assets.auxodev.dk/assets/a97cf6ea-7b79-4b87-946d-9f6d75bc924c/hegn-vinkelvej800x600-afc2859fa0-94.jpg'
    ]
  }
];

const CATEGORIES = [
  'Alle',
  'Altan',
  'Hegn',
  'Konstruktioner',
  'Rækværk',
  'Skydeport',
  'Trapper',
  'Andet'
];

export default function GallerySection({
  targetSlug = null,
  showFilter = true,
  title = 'Galleri',
  subtitle = 'Her kan du se eksempler på det arbejde vi har udført for vores kunder',
  limit = null
}) {
  const [galleries, setGalleries] = useState(FALLBACK_GALLERY);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    // Fetch dynamically from CMS collection 'galleri'
    fetch('https://api.dev.auxo.dk/public/v2/content/galleri')
      .then((res) => {
        if (!res.ok) throw new Error('CMS request failed');
        return res.json();
      })
      .then((data) => {
        if (data && data.items && data.items.length > 0) {
          setGalleries(data.items);
        }
      })
      .catch(() => {
        // Keeps fallback
      });
  }, []);

  // Compute filtered images
  const flattenedImages = useMemo(() => {
    let filtered = galleries;
    if (targetSlug && targetSlug !== 'alle') {
      filtered = galleries.filter(
        (g) => g.gallery_slug === targetSlug || g.category?.toLowerCase() === targetSlug.toLowerCase()
      );
      if (filtered.length === 0) {
        filtered = galleries;
      }
    } else if (selectedCategory !== 'Alle') {
      filtered = galleries.filter((g) => {
        const cat = g.category?.toLowerCase() || '';
        const selected = selectedCategory.toLowerCase();
        if (selected === 'skydeport') {
          return cat.includes('skydeport') || cat.includes('port');
        }
        return cat === selected;
      });
    }

    const list = [];
    const seenUrls = new Set();

    filtered.forEach((item) => {
      if (Array.isArray(item.images)) {
        item.images.forEach((imgUrl) => {
          if (imgUrl && !seenUrls.has(imgUrl)) {
            seenUrls.add(imgUrl);
            list.push({
              url: imgUrl,
              title: item.title,
              category: item.category,
              description: item.description
            });
          }
        });
      }
    });

    return limit ? list.slice(0, limit) : list;
  }, [galleries, targetSlug, selectedCategory, limit]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev > 0 ? prev - 1 : flattenedImages.length - 1));
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev < flattenedImages.length - 1 ? prev + 1 : 0));
  };

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(title || subtitle) && (
          <div className="text-center max-w-3xl mx-auto mb-10">
            {title && (
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="mt-3 text-base sm:text-lg text-stone-600">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* Category filter tabs if showFilter is true */}
        {showFilter && !targetSlug && (
          <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
            {CATEGORIES.map((cat) => {
              const isActive = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-stone-900 text-white shadow-sm'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        )}

        {/* Image Grid */}
        {flattenedImages.length === 0 ? (
          <div className="text-center py-12 bg-stone-100 rounded-xl">
            <p className="text-stone-500 font-medium">Ingen billeder fundet i denne kategori.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {flattenedImages.map((img, idx) => (
              <div
                key={`${img.url}-${idx}`}
                onClick={() => openLightbox(idx)}
                className="group relative bg-stone-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 aspect-[4/3] cursor-pointer"
              >
                <img
                  src={img.url}
                  alt={img.title || 'Galleri billede'}
                  className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300 block"
                  style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-stone-950/0 group-hover:bg-stone-950/30 transition-colors flex items-center justify-center">
                  <div className="w-11 h-11 rounded-full bg-stone-900/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-md transform scale-90 group-hover:scale-100">
                    <i className="fa-solid fa-magnifying-glass-plus text-base" aria-hidden="true"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Lightbox
        images={flattenedImages}
        currentIndex={lightboxIndex}
        onClose={closeLightbox}
        onPrev={prevImage}
        onNext={nextImage}
      />
    </section>
  );
}
